<script>
  /**
   * Dropdown component with popover functionality
   * @example
   * To programmatically close the popover from child components:
   * <Dropdown>
   *   {#snippet children(popover_ref)}
   *     <button onclick={() => popover_ref?.hidePopover()}>
   *       Close
   *     </button>
   *   {/snippet}
   * </Dropdown>
   */

  import { nanoid } from '#lib/util.js';
  import Popover from '#lib/Popover.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';

  let {
    /** @type {string} Unique identifier for the dropdown */
    id = `dropdown_${nanoid()}`,
    /** @type {HTMLButtonElement} Reference to the trigger button element */
    ref = $bindable(),
    /** @type {Popover} Reference to the popover element */
    popover_ref = $bindable(),
    /** @type {boolean} Read only state of the dropdown's open state */
    is_open = $bindable(false),
    /** @type {string} Text to display in the trigger button */
    label,
    /** @type {string=} Icon to display at the start of the button */
    start_icon,
    /** @type {string} Icon to display at the end of the button */
    end_icon,
    /** @type {boolean} Whether to show the dropdown arrow */
    show_arrow = true,
    /** @type {string} Button styling variant */
    variant = 'brand subtle-outlined',
    /** @type {boolean} Whether the dropdown is disabled */
    disabled = false,
    /** @type {() => void} Callback when dropdown opens */
    on_open,
    /** @type {() => void} Callback when dropdown closes */
    on_close,
    /** @type {Snippet} Content to render inside the dropdown */
    children,
    /** @type {'top'|'top-start'|'top-end'|'top-flush'|'bottom'|'bottom-start'|'bottom-end'|'bottom-flush'|'left'|'left-start'|'left-end'|'right'|'right-start'|'right-end'|'center'} Defines the popover's position relative to its anchor */
    placement = 'bottom',
    /** @type {number} Expected content width for flip logic. */
    expected_width,
    /** @type {number} Expected content height for flip logic. */
    expected_height,
  } = $props();

  function handle_toggle() {
    if (!is_open) {
      on_open?.();
    } else {
      on_close?.();
    }
  }
</script>

<button 
  bind:this={ref}
  {id}
  {disabled}
  popovertarget={`${id}_popover`}
  class="dropdown-trigger"
  onclick={handle_toggle}
>
  <ButtonLabel
    {variant}
    {start_icon}
    end_icon={show_arrow ? (is_open ? 'small-arrow-up' : 'small-arrow-down') : end_icon}
    align={(show_arrow || end_icon) ? 'between' : undefined}
    text={label}
  />
</button>

<Popover
  anchor={ref}
  id={`${id}_popover`}
  bind:is_open
  bind:ref={popover_ref}
  class="dropdown-popover"
  {placement}
  {expected_width}
  {expected_height}
>
  {@render children(popover_ref)}
</Popover>